import { redirect } from '@tanstack/react-router';
import { type FileRouteTypes } from '~/routeTree.gen';

import { type PermissionMainEntity } from '~/types/permission';
import { type RouterContext } from '~/types/router.type';

import {
  getUserDataQueryOptions,
  type SelectedUserData,
  selectUserData,
} from '~/data/user';

type RouteProtectionConfig = {
  /**
   * Required permissions for accessing this route.
   * User must have at least one matching permission.
   */
  permissions?: PermissionMainEntity[];

  /**
   * Feature flag(s) that must be enabled for access.
   * When an array is provided, all flags must be enabled.
   */
  featureFlag?: string | string[];

  /**
   * Feature flag(s) that, when enabled, disable access to this route.
   * When an array is provided, any single flag being enabled denies access.
   */
  disablingFeatureFlag?: string | string[];

  /**
   * If true, only Vestigas support users can access this route.
   */
  vestigasSupportAccess?: boolean;

  /**
   * Route path for logging purposes (e.g., '/deliveries').
   */
  routePath: FileRouteTypes['fullPaths'];
};

/**
 * Creates a beforeLoad hook that checks if the user is authorized to access a route
 * based on the provided permissions and feature flags.
 *
 * @param config - Route protection configuration
 * @returns A beforeLoad function that redirects to /unauthorized if the user lacks permissions
 *
 * @example
 * export const Route = createFileRoute('/deliveries/')({
 *   component: DeliveryOverview,
 *   beforeLoad: createProtectedRoute({
 *     routePath: '/deliveries',
 *     permissions: ['dln_in.'],
 *   }),
 * })
 */
export function createProtectedRoute(config: RouteProtectionConfig) {
  const {
    permissions,
    featureFlag,
    disablingFeatureFlag,
    vestigasSupportAccess,
  } = config;

  return async ({ context }: { context: RouterContext }) => {
    const { auth, queryClient } = context;

    const userId = auth.getUserIdFromAccessToken();
    const isAuthenticated = Boolean(userId);

    // If not authenticated, let AppInitializer handle OIDC login flow
    // Don't redirect to /unauthorized - that's only for authorization failures
    if (!isAuthenticated) {
      return { user: undefined };
    }

    // Get user data from query cache (loaded by AppInitializer)
    const userDataQueryOptions = getUserDataQueryOptions({
      isAuthenticated: true,
      includeCompanyInfo: true,
    });

    const userData = await queryClient.ensureQueryData(userDataQueryOptions);
    const selectedUserData = selectUserData(userData);

    // This should not happen if authenticated, but handle gracefully
    if (!userData) {
      redirect({ to: '/unauthorized', throw: true });
    }

    // Check permissions - redirect to /unauthorized only for authorization failures
    const hasPermission = checkUserPermissions(selectedUserData, permissions);

    if (!hasPermission) {
      redirect({ to: '/unauthorized', throw: true });
    }

    // If a route has permissions, anyone with those permissions can access, regardless of Vestigas status.
    if (
      vestigasSupportAccess &&
      !permissions?.length &&
      !userData.isVestigasSupport
    ) {
      redirect({ to: '/unauthorized', throw: true });
    }

    validateFeatureFlags(selectedUserData, featureFlag, disablingFeatureFlag);

    return { user: selectedUserData };
  };
}

/**
 * Checks if user has the required permissions
 */
export function checkUserPermissions(
  userData: SelectedUserData,
  requiredPagePermissions?: string[],
): boolean {
  if (!requiredPagePermissions || requiredPagePermissions.length === 0) {
    return true;
  }

  const userPerms = userData.userPermissions ?? [];

  for (const userPermission of userPerms) {
    for (const routePermission of requiredPagePermissions) {
      if (String(userPermission).includes(routePermission)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Validates feature flags and throws redirect if validation fails
 */
export function validateFeatureFlags(
  userData: SelectedUserData,
  requiredFeatureFlag?: string | string[],
  disablingFeatureFlag?: string | string[],
): void {
  // Check required feature flag(s) - all must be enabled to allow access
  if (
    requiredFeatureFlag &&
    ![requiredFeatureFlag]
      .flat()
      .every((featureFlag) => userData.featureFlags?.[featureFlag] === true)
  ) {
    redirect({ to: '/unauthorized', throw: true });
  }

  // Check disabling feature flag(s) - any must be enabled to deny access
  if (
    disablingFeatureFlag &&
    [disablingFeatureFlag]
      .flat()
      .some((featureFlag) => userData.featureFlags?.[featureFlag] === true)
  ) {
    redirect({ to: '/unauthorized', throw: true });
  }
}
